import { createTheme } from '@mui/material/styles'
import { focusStyle, focusStyleBorder } from './styles/common'

const createTypographyStyle = ({
	color,
	fontSize,
	fontWeight,
	lineHeight,
	letterSpacing = 'normal',
	textDecoration = 'none',
	element = 'p'
}) => ({
	color,
	fontFamily: 'Gotham',
	fontSize,
	fontStyle: 'normal',
	fontWeight,
	lineHeight,
	letterSpacing,
	textDecoration,
	element
})

const breakpointTheme = createTheme({
	breakpoints: {
		values: {
			xs: 0, // Mobile
			sm: 600, // Tablet
			md: 900, // Desktop
			lg: 1200, // Desktop
			xl: 1536 // Desktop
		}
	}
})
const theme = createTheme(breakpointTheme, {
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					width: '100%',
					marginBottom: '2rem',
					borderColor: '#4A4A4A'
				},
				notchedOutline: {
					borderColor: '#4A4A4A'
				}
			}
		},
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true,
				disableTouchRipple: true,
				focusRipple: false
			},
			styleOverrides: {
				root: {
					...focusStyle
				}
			}
		},
		MuiTab: {
			styleOverrides: {
				root: {
					fontFamily: 'Gotham',
					...focusStyleBorder,
					'&.Mui-selected.Mui-focusVisible': {
						borderColor: 'white'
					}
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					flexBasis: 0,
					flexShrink: 1,
					fontSize: '0.875rem', // 14px
					fontStyle: 'normal',
					fontWeight: '325',
					lineHeight: '1.25rem', // 20px
					letterSpacing: '0.0125rem', // 0.2px
					padding: '0.875rem 0!important', // 14px
					[breakpointTheme.breakpoints.up('sm')]: {
						padding: '1.5rem 0!important', // 28px
						fontSize: '1.125rem!important' // 18px
					},

					margin: 0,
					minHeight: 'unset'
				}
			}
		},
		MuiLink: {
			styleOverrides: {
				root: {
					...focusStyle
				}
			}
		},
		MuiInputBase: {
			styleOverrides: {
				input: {
					// ...focusStyle
				}
			}
		}
	},
	palette: {
		primary: {
			main: '#2E2C70',
			tint2: '#58568D',
			tint3: '#D5D5E2'
		},
		secondary: {
			main: '#B03A00',
			orange: '#B03A00'
		},
		tertiary: {
			main: '#00AAFF'
		},
		white: '#FFFFFF',
		black: '#000000',
		error: {
			main: '#B03A00'
		},
		warning: {
			main: '#f39c12'
		},
		info: {
			main: '#8e44ad'
		},
		success: {
			main: '#2ecc71'
		},
		background: {
			main: '#f4f4f4',
			white: '#ffffff',
			red: '#e74c3c'
		},
		text: {
			primary: '#2c3e50',
			grey: '#4a4a4a'
		}
	},
	typography: {
		fontFamily: 'Gotham, sans-serif',
		desktopH1Primary: createTypographyStyle({
			color: '#2E2C70',
			element: 'h1',
			fontSize: '3.625rem', // 58px
			fontWeight: 600,
			lineHeight: '3.75rem' // 60px
		}),
		desktopH1: createTypographyStyle({
			color: '#FFFFFF',
			element: 'h1',
			fontSize: '3.625rem', // 58px
			fontWeight: 600,
			lineHeight: '3.75rem' // 60px
		}),
		responsiveH1Primary: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '2.25rem', // 26px
			fontWeight: 600,
			lineHeight: '2.375rem', // 38px
			letterSpacing: '0.009rem' // 0.144px
		}),
		responsiveH1: createTypographyStyle({
			color: '#FFFFFF',
			element: 'h1',
			fontSize: '2.25rem', // 26px
			fontWeight: 600,
			lineHeight: '2.375rem', // 38px
			letterSpacing: '0.009rem' // 0.144px
		}),
		desktopH2Primary: createTypographyStyle({
			color: '#2E2C70',
			element: 'h2',
			fontSize: '2.625rem', // 42px
			fontWeight: 600,
			lineHeight: '2.75rem', // 44px
			letterSpacing: '0.0154rem' // 0.247px
		}),
		responsiveH2Primary: createTypographyStyle({
			color: '#2E2C70',
			element: 'h2',
			fontSize: '1.75rem', // 28px
			fontWeight: 600,
			lineHeight: '1.875rem', // 30px
			letterSpacing: '0.0103rem' // 0.247px
		}),
		desktopH2White: createTypographyStyle({
			color: '#FFFFFF',
			fontSize: '2.625rem', // 42px
			fontWeight: 600,
			lineHeight: '2.75rem', // 44px
			letterSpacing: '0.0154rem' // 0.247px
		}),
		responsiveH2White: createTypographyStyle({
			color: '#FFFFFF',
			element: 'h2',
			fontSize: '1.75rem', // 28px
			fontWeight: 600,
			lineHeight: '1.875rem', // 30px
			letterSpacing: '0.0103rem' // 0.247px
		}),
		desktopH3: createTypographyStyle({
			color: '#2E2C70',
			element: 'h3',
			fontSize: '1.875rem', // 30px
			fontWeight: 600,
			lineHeight: '2.3125rem', // 37px
			letterSpacing: '0.0171rem' // 0.273px
		}),
		responsiveH3: createTypographyStyle({
			color: '#2E2C70',
			element: 'h3',
			fontSize: '1.25rem', // 20px
			fontWeight: 400,
			lineHeight: '1.5rem', // 24px
			letterSpacing: '0.0171rem' // 0.273px
		}),
		desktopH4: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '1.5rem', // 24px
			fontWeight: 600,
			lineHeight: '1.625rem', // 26px
			letterSpacing: '0.0176rem' // 0.282px
		}),
		responsiveH4: createTypographyStyle({
			color: '#000000',
			element: 'h4',
			fontSize: '1.125rem', // 18px
			fontWeight: 600,
			lineHeight: '1.5rem', // 24px
			letterSpacing: '0.01325rem' // 0.212px
		}),
		desktopH5Black: createTypographyStyle({
			color: '#000000',
			element: 'h5',
			fontSize: '1.25rem', // 20px
			fontWeight: 600,
			lineHeight: '1.5rem', // 24px
			letterSpacing: '0.0176rem' // 0.282px
		}),
		desktopH5White: createTypographyStyle({
			color: '#FFFFFF',
			element: 'h5',
			fontSize: '1.25rem', // 20px
			fontWeight: 600,
			lineHeight: '1.5rem', // 24px
			letterSpacing: '0.0176rem' // 0.282px
		}),
		responsiveH5Black: createTypographyStyle({
			color: '#000000',
			fontSize: '1rem', // 16px
			fontWeight: 600,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.01175rem' // 0.188px
		}),
		responsiveH5Primary: createTypographyStyle({
			color: '#2E2C70',
			element: 'h5',
			fontSize: '1rem', // 16px
			fontWeight: 600,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.01175rem' // 0.188px
		}),
		bodySmall: createTypographyStyle({
			color: '#4A4A4A',
			fontSize: '0.875rem', // 14px
			fontWeight: 325,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodySmallPrimary: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '0.875rem', // 14px
			fontWeight: 325,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodySmallWhite: createTypographyStyle({
			color: '#ffffff',
			fontSize: '0.875rem', // 14px
			fontWeight: 325,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodySmallBlackUnderline: createTypographyStyle({
			color: '#000000',
			fontSize: '0.875rem', // 14px
			fontWeight: 325,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem', // 0.2px
			textDecoration: 'underline'
		}),
		bodyMediumPrimary: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '1.125rem', // 18px
			fontWeight: 325,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyMediumWhite: createTypographyStyle({
			color: '#FFFFFF',
			fontSize: '1.125rem', // 18px
			fontWeight: 325,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyMediumBlack: createTypographyStyle({
			color: '#000000',
			fontSize: '1.125rem', // 18px
			fontWeight: 325,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyMediumBlackUnderline: createTypographyStyle({
			color: '#000000',
			fontSize: '1.125rem', // 18px
			fontWeight: 325,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem', // 0.2px
			textDecoration: 'underline'
		}),
		bodyMediumLink: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '1.125rem', // 18px
			fontWeight: 525,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem', // 0.2px
			textDecoration: 'underline'
		}),
		bodyMediumGrey: createTypographyStyle({
			color: '#4A4A4A',
			fontSize: '1.125rem', // 18px
			fontWeight: 325,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyLargePrimary: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '1.375rem', // 22px
			fontWeight: 325,
			lineHeight: '2rem', // 32px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyLargeGrey: createTypographyStyle({
			color: '#A4A4A4',
			fontSize: '1.375rem', // 22px
			fontWeight: 400,
			lineHeight: '2rem', // 32px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyLargeBlack: createTypographyStyle({
			color: '#000000',
			fontSize: '1.375rem', // 22px
			fontWeight: 400,
			lineHeight: '2rem', // 32px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyLargeWhite: createTypographyStyle({
			color: '#FFFFFF',
			fontSize: '1.375rem', // 22px
			fontWeight: 325,
			lineHeight: '2rem', // 32px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		searchResponse: createTypographyStyle({
			color: '#000000',
			fontSize: '1.125rem', // 18px
			fontWeight: 425,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		responsiveSearchResponse: createTypographyStyle({
			color: '#000000',
			fontSize: '0.875rem', // 14px
			fontWeight: 425,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		toolkitTitles: createTypographyStyle({
			color: '#000000',
			fontSize: '0.875rem', // 14px
			fontWeight: 450,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		filterSortHeadings: createTypographyStyle({
			color: '#000000',
			fontSize: '1rem', // 16px
			fontWeight: 400,
			lineHeight: '1.75rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		filterSortOptions: createTypographyStyle({
			color: '#000000',
			fontSize: '0.875rem', // 14px
			fontWeight: 300,
			lineHeight: '1.75rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		copyrightFooterText: createTypographyStyle({
			color: '#ffffff',
			fontSize: '0.75rem', // 12px
			fontWeight: 325,
			lineHeight: '1.25rem', // 20px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		desktopHeadline: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '1.125rem', // 18px
			fontWeight: 400,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		}),
		responsiveHeadline: createTypographyStyle({
			color: '#2E2C70',
			fontSize: '0.5625rem', // 9px
			fontWeight: 400,
			lineHeight: 'normal',
			letterSpacing: '0.0125rem' // 0.2px
		}),
		bodyMediumLabel: createTypographyStyle({
			fontSize: '1.125rem', // 18px
			fontStyle: 'normal',
			fontWeight: 400,
			lineHeight: '1.75rem', // 28px
			letterSpacing: '0.0125rem' // 0.2px
		})
	}
})

export default theme
